import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from '../pages/LandingPage';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import AboutPage from '../pages/AboutPage';
import ContactPage from '../pages/ContactPage';
import HowItWorksPage from '../pages/HowItWorksPage';
import PricingPage from '../pages/PricingPage';
import FeaturesPage from '../pages/FeaturesPage';
import FAQPage from '../pages/FAQPage';
import BlogPage from '../pages/BlogPage';
import AIvsHumanCoach from '../pages/blog/AIvsHumanCoach';
import ADHDTaskManagement from '../pages/blog/ADHDTaskManagement';
import GenericBlogPost from '../pages/blog/GenericBlogPost';
import NotFoundPage from '../pages/NotFoundPage';
import AdminPage from '../pages/AdminPage';
import AccountPage from '../pages/AccountPage';
import BillingReturnPage from '../pages/BillingReturnPage';
import ReferralInvitePage from '../pages/ReferralInvitePage';
import { useAuth } from '../hooks/useAuth';
import Login from './Login';
import { trackPageView } from '../utils/analytics';
import AuthEntry from './AuthEntry';
import CompanionApp from './companion/CompanionApp';

const AccountRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/account${search}`} replace />;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    trackPageView(pagePath);
  }, [location]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/ai-accountability-vs-human-coach" element={<AIvsHumanCoach />} />
      <Route path="/blog/adhd-task-management-ai" element={<ADHDTaskManagement />} />
      <Route path="/blog/:slug" element={<GenericBlogPost />} />

      {/* Tasks are an optional authenticated companion; account remains separate. */}
      <Route path="/account" element={<AccountPage />} />
      <Route path="/r/:code" element={<ReferralInvitePage />} />
      <Route path="/billing/success" element={<BillingReturnPage />} />
      <Route path="/billing/cancel" element={<BillingReturnPage cancelled />} />
      <Route path="/app/*" element={<AuthEntry><CompanionApp /></AuthEntry>} />

      {/* Owner/admin dashboard - hidden route; handles its own auth + 403 state */}
      <Route path="/admin" element={<AdminAuthRoute />} />

      {/* Redirect old routes */}
      <Route path="/login" element={<AccountRedirect />} />
      <Route path="/signup" element={<AccountRedirect />} />
      <Route path="/sign-up" element={<AccountRedirect />} />
      <Route path="/customer" element={<AccountRedirect />} />
      <Route path="/customer/*" element={<AccountRedirect />} />
      <Route path="/sms-signup" element={<Navigate to="/" replace />} />

      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const AdminAuthRoute: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  if (isLoading) return <div className="min-h-screen flex items-center justify-center">Loading admin access…</div>;
  if (!isAuthenticated) return <Login onLoginSuccess={login} />;
  return <AdminPage />;
};

const AppRouter: React.FC = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

export default AppRouter;
